
import React, { useEffect, lazy, Suspense } from 'react';
import Header from '@/components/HeaderUpdated';
import Footer from '@/components/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ProtectedRoute from '@/components/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import EditProfileForm from '@/components/UserAccount/EditProfileForm';
import StoreSelector from '@/components/UserAccount/StoreSelector';
import OrderHistory from '@/components/UserAccount/OrderHistory';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, Coins, ShoppingBag, LogOut, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Lazy load StorePointConnector to avoid initialization issues
const StorePointConnector = lazy(() =>
  import('@/components/StorePoint/StorePointConnector').then(module => ({
    default: module.StorePointConnector
  }))
);

const VALID_TABS = ['dados', 'conta', 'orders'] as const;
type TabKey = typeof VALID_TABS[number];
const isValidTab = (tab: string | null): tab is TabKey =>
  !!tab && (VALID_TABS as readonly string[]).includes(tab as TabKey);

const MyAccount = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isValidTab(tabParam) ? tabParam : 'conta';
  const { userProfile, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    toast.success(t('myAccountPage.logoutSuccess'));
    navigate('/');
  };

  const tabLabels = {
    dados: t('myAccountPage.tabs.profile'),
    conta: t('myAccountPage.tabs.storePoint'),
    orders: t('myAccountPage.tabs.orders'),
  } as const;

  const handleTabChange = (value: string) => {
    if (isValidTab(value)) {
      setSearchParams({ tab: value }, { replace: true });
    }
  };

  useEffect(() => {
    if (!isValidTab(tabParam)) {
      setSearchParams({ tab: 'conta' }, { replace: true });
    }
  }, [tabParam, setSearchParams]);

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-brand-darker text-white">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="mx-auto max-w-7xl">
            {/* Page Title */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                <User size={28} className="text-brand-red" />
                {t('myAccountPage.title')}
              </h1>
              <p className="text-gray-400">
                {t('myAccountPage.welcome')} {userProfile?.username || t('myAccountPage.defaultName')}
              </p>
            </div>

            <div className="flex flex-col lg:flex-row items-start gap-6">
              {/* Mobile Navigation Dropdown */}
              <div className="w-full lg:hidden mb-6">
                <Card className="bg-brand-gray border-none">
                  <CardContent className="p-4">
                    <Select value={activeTab} onValueChange={handleTabChange}>
                      <SelectTrigger className="w-full bg-brand-darker border-gray-600 text-white h-12">
                        <div className="flex items-center gap-3">
                          {activeTab === 'dados' && <User size={18} />}
                          {activeTab === 'conta' && <Coins size={18} />}
                          {activeTab === 'orders' && <ShoppingBag size={18} />}
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="bg-brand-gray border-gray-600">
                        <SelectItem value="dados" className="text-white hover:bg-brand-darker">
                          <div className="flex items-center gap-3">
                            <User size={18} />
                            {tabLabels.dados}
                          </div>
                        </SelectItem>
                        <SelectItem value="conta" className="text-white hover:bg-brand-darker">
                          <div className="flex items-center gap-3">
                            <Coins size={18} />
                            {tabLabels.conta}
                          </div>
                        </SelectItem>
                        <SelectItem value="orders" className="text-white hover:bg-brand-darker">
                          <div className="flex items-center gap-3">
                            <ShoppingBag size={18} />
                            {tabLabels.orders}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {/* Mobile Logout Button */}
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="w-full justify-start px-4 py-3 mt-3 text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                    >
                      <LogOut size={18} className="mr-3" />
                      {t('myAccountPage.logout')}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Desktop Sidebar Navigation */}
              <Card className="hidden lg:block w-72 bg-brand-gray border-none shrink-0">
                <CardContent className="p-4">
                  <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                    <TabsList className="h-auto flex flex-col w-full bg-transparent gap-1 p-0">
                      <TabsTrigger
                        value="dados"
                        className={`w-full justify-start px-4 py-3 ${activeTab === 'dados' ? 'bg-brand-darker text-white' : 'bg-transparent text-gray-300'} hover:bg-brand-darker/80 border-none transition-colors`}
                      >
                        <User size={18} className="mr-3" />
                        {tabLabels.dados}
                      </TabsTrigger>
                      <TabsTrigger
                        value="conta"
                        className={`w-full justify-start px-4 py-3 ${activeTab === 'conta' ? 'bg-brand-darker text-white' : 'bg-transparent text-gray-300'} hover:bg-brand-darker/80 border-none transition-colors`}
                      >
                        <Coins size={18} className="mr-3" />
                        {tabLabels.conta}
                      </TabsTrigger>
                      <TabsTrigger
                        value="orders"
                        className={`w-full justify-start px-4 py-3 ${activeTab === 'orders' ? 'bg-brand-darker text-white' : 'bg-transparent text-gray-300'} hover:bg-brand-darker/80 border-none transition-colors`}
                      >
                        <ShoppingBag size={18} className="mr-3" />
                        {tabLabels.orders}
                      </TabsTrigger>
                    </TabsList>
                  </Tabs>

                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="w-full justify-start px-4 py-3 text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-colors"
                    >
                      <LogOut size={18} className="mr-3" />
                      {t('myAccountPage.logout')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Main Content Area */}
              <div className="w-full flex-1">
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                  <TabsContent value="dados" className="mt-0">
                    <EditProfileForm />
                  </TabsContent>
                  <TabsContent value="conta" className="mt-0">
                    <Suspense fallback={
                      <div className="flex items-center justify-center p-8">
                        <Loader2 className="h-8 w-8 animate-spin text-brand-red" />
                      </div>
                    }>
                      <StorePointConnector />
                    </Suspense>
                    <StoreSelector />
                  </TabsContent>
                  <TabsContent value="orders" className="mt-0">
                    <OrderHistory />
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </ProtectedRoute>
  );
};

export default MyAccount;
